import { createStore, filterNil, select, withProps } from '@ngneat/elf';
import { joinRequestResult } from '@ngneat/elf-requests';
import { InitializationState } from '@tmx/core';
import { trackRequest } from '@tmx/core/elf-utils';
import {
  CompanyEmailSettings,
  CompanyLicenseFeatures,
  CompanySettings,
  CountryCodeSettings,
  Language,
  PartnerSettings,
} from '@tmx/data-access';
import { filter, map, shareReplay } from 'rxjs';

import {
  SAMSARA_DRIVER_PARTNER_ID,
  SAMSARA_PARTNER_ID,
} from '../../models/constants';
import { getSavedPageSize } from '../../utils/get-saved-page-size';
import { getUserTypeDisplayNameKey } from '../../utils/get-usertype-display-name-key';
import { requestCompletedAndSucceeded } from '../../utils/request-succeeded';
import { accountSummarySnapshot } from '../ams/ams.repository';
import { isFeatureEnabled } from '../feature-flags/feature-flags.repository';

type RequestKeys =
  | 'check-weather-alerts-enabled'
  | 'fetch-account-language'
  | 'fetch-company-email-settings'
  | 'fetch-company-license-features'
  | 'fetch-company-settings'
  | 'fetch-country-codes'
  | 'fetch-languages'
  | 'partner-settings'
  | 'save-company-email-settings'
  | 'update-company-settings';

interface AppSettingsProps {
  appSettingsState: InitializationState;
  companyEmailSettings: CompanyEmailSettings | null;
  companyLicenseFeatures: CompanyLicenseFeatures | null;
  companySettings: CompanySettings | null;
  countryCodes: CountryCodeSettings[];
  dataTablePageSize: number;
  defaultLanguage: string;
  languages: Language[];
  linkedDataState: 'driver' | 'none' | 'vehicle';
  partnerSettings: null | PartnerSettings;
  weatherAlertsEnabled: boolean;
}

const appSettingsStore = createStore(
  { name: 'app-settings' },
  withProps<AppSettingsProps>({
    appSettingsState: 'loading',
    companySettings: null,
    partnerSettings: null,
    dataTablePageSize: getSavedPageSize(),
    companyEmailSettings: null,
    countryCodes: [],
    languages: [],
    defaultLanguage: 'en',
    linkedDataState: 'none',
    companyLicenseFeatures: null,
    weatherAlertsEnabled: false,
  })
);

/** Snapshots */

export const appSettingsStoreSnapshot = () => appSettingsStore.getValue();

export function companySettingsSnapshot() {
  const { companySettings } = appSettingsStoreSnapshot();
  if (companySettings === null) {
    throw new Error(
      'companySettingsSnapshot function called before company settings initialized.'
    );
  }

  return companySettings;
}

export function partnerSettingsSnapshot() {
  const { partnerSettings } = appSettingsStoreSnapshot();
  if (partnerSettings === null) {
    throw new Error(
      'partnerSettingsSnapshot function called before partner settings initialized.'
    );
  }

  return partnerSettings;
}

export function companyLicenseFeaturesSnapshot() {
  const { companyLicenseFeatures } = appSettingsStoreSnapshot();
  if (companyLicenseFeatures === null) {
    throw new Error(
      'companyLicenseFeaturesSnapshot function called before company license features initialized.'
    );
  }

  return companyLicenseFeatures;
}

export const canSyncAssets = () => {
  const { companyType, eldPartnerType } = accountSummarySnapshot();
  const { isFleetPortalEnabled } = companySettingsSnapshot();

  if (!isFleetPortalEnabled) {
    return false;
  }

  if (
    eldPartnerType === 'Common Partner Hub Integration' &&
    (companyType === 'Trial' || companyType === 'Paying Customer')
  ) {
    return true;
  }

  return false;
};

export const isSyncOn = () =>
  canSyncAssets() && companySettingsSnapshot().partnerIntegrationSync;

export const linkedDataEnabled = () =>
  appSettingsStoreSnapshot().linkedDataState !== 'none';

export const vehicleIntegrationLinkedDataEnabled = () =>
  appSettingsStoreSnapshot().linkedDataState === 'vehicle';

export const driverIntegrationLinkedDataEnabled = () =>
  appSettingsStoreSnapshot().linkedDataState === 'driver';

export const dataTablePageSizeSnapshot = () =>
  appSettingsStore.getValue().dataTablePageSize;

export const userTypeDisplayNameKey = () =>
  getUserTypeDisplayNameKey(companySettingsSnapshot().userType);

/** Request Results Trackers */

export const trackAppSettingsRequests = <T>(keys: RequestKeys[]) =>
  trackRequest<T, RequestKeys>(keys);

export const joinAppSettingsRequests = <T>(keys: RequestKeys[]) =>
  joinRequestResult<T>(keys);

/** Exposed Variables and Observables */

export const isFleetPortalOnly = () =>
  companySettingsSnapshot().isFleetOnlyVersion;

export const appSettingsState$ = appSettingsStore.pipe(
  select(({ appSettingsState }) => appSettingsState)
);

export const companyEmailSettingsInitialized$ = appSettingsStore.pipe(
  select(({ companyEmailSettings }) => companyEmailSettings),
  joinAppSettingsRequests(['fetch-company-email-settings']),
  requestCompletedAndSucceeded()
);

export const companyEmailSettingsSaved$ = appSettingsStore.pipe(
  select(({ companyEmailSettings }) => companyEmailSettings),
  joinAppSettingsRequests(['save-company-email-settings']),
  requestCompletedAndSucceeded()
);

export const companySettings$ = appSettingsStore.pipe(
  select(({ companySettings }) => companySettings),
  filterNil(),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const companySettingsInitialized$ = appSettingsStore.pipe(
  select(({ companySettings }) => companySettings),
  joinAppSettingsRequests(['fetch-company-settings']),
  requestCompletedAndSucceeded()
);

export const companySettingsUpdated$ = appSettingsStore.pipe(
  select(({ companySettings }) => companySettings),
  joinAppSettingsRequests(['update-company-settings']),
  requestCompletedAndSucceeded()
);

export const partnerSettingsInitialized$ = appSettingsStore.pipe(
  select(({ partnerSettings }) => partnerSettings),
  joinAppSettingsRequests(['partner-settings']),
  requestCompletedAndSucceeded()
);

export const companyLicenseFeaturesInitialized$ = appSettingsStore.pipe(
  select(({ companyLicenseFeatures }) => companyLicenseFeatures),
  joinAppSettingsRequests(['fetch-company-license-features']),
  requestCompletedAndSucceeded()
);

export const countryCodesLoaded$ = appSettingsStore.pipe(
  select(({ countryCodes }) => countryCodes),
  joinAppSettingsRequests(['fetch-country-codes']),
  requestCompletedAndSucceeded()
);

export const languagesLoaded$ = appSettingsStore.pipe(
  select(({ countryCodes }) => countryCodes),
  joinAppSettingsRequests(['fetch-languages']),
  requestCompletedAndSucceeded()
);

export const accountLanguageLoaded$ = appSettingsStore.pipe(
  select(({ defaultLanguage }) => defaultLanguage),
  joinAppSettingsRequests(['fetch-account-language']),
  requestCompletedAndSucceeded()
);

export const checkedWeatherAlertsAreEnabled$ = appSettingsStore.pipe(
  select(({ weatherAlertsEnabled }) => weatherAlertsEnabled),
  joinAppSettingsRequests(['check-weather-alerts-enabled']),
  filter(({ status }) => status !== 'idle' && status !== 'loading'),
  map(({ isLoading }) => !isLoading)
);

export const partnerSettings$ = appSettingsStore.pipe(
  select(({ partnerSettings }) => partnerSettings),
  filterNil(),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const countryCodes$ = appSettingsStore.pipe(
  select(({ countryCodes }) => countryCodes),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const languages$ = appSettingsStore.pipe(
  select(({ languages }) => languages),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const userTypeDisplayNameKey$ = companySettings$.pipe(
  map(({ userType }) => getUserTypeDisplayNameKey(userType))
);

/** Updater Functions */

export function updateAppSettingsState(appSettingsState: InitializationState) {
  appSettingsStore.update((state) => ({
    ...state,
    appSettingsState,
  }));
}

export function updateCompanySettings(companySettings: CompanySettings | null) {
  appSettingsStore.update((state) => ({
    ...state,
    companySettings,
  }));
}

export function updatePartnerSettings(partnerSettings: null | PartnerSettings) {
  appSettingsStore.update((state) => ({
    ...state,
    partnerSettings,
  }));
}

export function updateCompanyEmailSettings(
  companyEmailSettings: CompanyEmailSettings | null
) {
  appSettingsStore.update((state) => ({
    ...state,
    companyEmailSettings,
  }));
}

export function updateDataTablePageSize(dataTablePageSize: number) {
  appSettingsStore.update((state) => ({
    ...state,
    dataTablePageSize,
  }));
}

export function updateCountryCodes(countryCodes: CountryCodeSettings[]) {
  appSettingsStore.update((state) => ({
    ...state,
    countryCodes,
  }));
}

export function updateDefaultLanguage(defaultLanguage: string) {
  appSettingsStore.update((state) => ({
    ...state,
    defaultLanguage,
  }));
}

export function updateLanguages(languages: Language[]) {
  appSettingsStore.update((state) => ({
    ...state,
    languages,
  }));
}

export function updateLinkedDataState() {
  const PARTNER_IDS = [SAMSARA_PARTNER_ID, SAMSARA_DRIVER_PARTNER_ID];
  let linkedDataState: 'driver' | 'none' | 'vehicle' = 'none';
  const featureFlagEnabled = isFeatureEnabled('samsara-messaging');

  if (
    PARTNER_IDS.includes(accountSummarySnapshot().partnerId ?? '') &&
    featureFlagEnabled
  ) {
    const { integrationType } = partnerSettingsSnapshot();
    if (integrationType === 'DriverId') {
      linkedDataState = 'driver';
    } else if (integrationType === 'VehicleId') {
      linkedDataState = 'vehicle';
    }
  }

  appSettingsStore.update((state) => ({
    ...state,
    linkedDataState,
  }));
}

export function updateCompanyLicenseFeatures(
  companyLicenseFeatures: CompanyLicenseFeatures
) {
  appSettingsStore.update((state) => ({
    ...state,
    companyLicenseFeatures,
  }));
}

export function updateWeatherAlertsEnabled(weatherAlertsEnabled: boolean) {
  appSettingsStore.update((state) => ({
    ...state,
    weatherAlertsEnabled,
  }));
}

export function resetAppSettingsStore() {
  appSettingsStore.reset();
}

/**
 * t(appSettingsRepository.integrationType.None)
 * t(appSettingsRepository.integrationType.MobileDeviceId)
 * t(appSettingsRepository.integrationType.DriverId)
 * t(appSettingsRepository.integrationType.VehicleId)
 */
